import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, ArrowRight, User, LayoutDashboard, Image, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { Logo } from '@/components/Logo';
import { MagneticButton } from '@/components/MagneticButton';
import { TextScramble } from '@/components/TextScramble';
import { MenuScanline } from '@/components/MenuScanline';
import { useAuth } from '@/context/AuthContext';
import { NAV, INTELLIGENCE_ENTRY, NAVBAR_OVERFLOW } from '@/constants/testIds';

// Desktop nav is split so the top-level row stays breathable: a few primary
// destinations stay always-visible, the rest live in a "More" dropdown.
// Mobile keeps the full flat list (NAV_LINKS) in its original order.
const PRIMARY_LINKS = [
  { to: '/', label: 'Home', testid: NAV.home },
  { to: '/galleries', label: 'Galleries', testid: NAV.galleries },
  { to: '/services', label: 'Services', testid: NAV.services },
  { to: '/journal', label: 'Journal', testid: NAV.journal },
];

const SECONDARY_LINKS = [
  { to: '/photographers', label: 'Photographers', testid: NAV.photographers },
  { to: '/how-it-works', label: 'How It Works', testid: NAV.howItWorks },
  { to: '/gear', label: 'Gear Guide', testid: NAV.gearLibrary },
  { to: '/plan', label: 'Plan', testid: INTELLIGENCE_ENTRY.navPlan },
  { to: '/about', label: 'About', testid: NAV.about },
  { to: '/contact', label: 'Contact', testid: NAV.contact },
];

const NAV_LINKS = [
  { to: '/', label: 'Home', testid: NAV.home },
  { to: '/galleries', label: 'Galleries', testid: NAV.galleries },
  { to: '/services', label: 'Services', testid: NAV.services },
  { to: '/photographers', label: 'Photographers', testid: NAV.photographers },
  { to: '/how-it-works', label: 'How It Works', testid: NAV.howItWorks },
  { to: '/gear', label: 'Gear Guide', testid: NAV.gearLibrary },
  { to: '/plan', label: 'Plan', testid: INTELLIGENCE_ENTRY.navPlan },
  { to: '/journal', label: 'Journal', testid: NAV.journal },
  { to: '/about', label: 'About', testid: NAV.about },
  { to: '/contact', label: 'Contact', testid: NAV.contact },
];

const mobileLinkClass = ({ isActive }) =>
  `relative text-sm tracking-[0.12em] uppercase transition-colors duration-200 py-1 ${
    isActive ? 'text-[hsl(var(--lxra-gold))]' : 'text-[hsl(var(--lxra-fg))] hover:text-[hsl(var(--lxra-gold))]'
  } after:absolute after:left-0 after:-bottom-1 after:h-[1px] after:bg-[hsl(var(--lxra-gold))] after:transition-all after:duration-300 ${
    isActive ? 'after:w-full' : 'after:w-0 hover:after:w-full'
  }`;

function isLinkActive(pathname, to) {
  if (to === '/') return pathname === '/';
  return pathname.startsWith(to);
}

// Tracks scroll distance so the navbar can switch into its scrolled
// (gradient) background state once the visitor moves away from the top.
// NOTE: the navbar intentionally stays always-visible (no hide-on-scroll)
// so the animated gradient sheen remains visible while browsing.
function useScrollBehavior() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return { scrolled };
}

export const Navbar = () => {
  const { scrolled } = useScrollBehavior();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuKey, setMenuKey] = useState(0);

  const accountLink =
    user?.role === 'admin'
      ? { to: '/admin', label: 'Dashboard', Icon: LayoutDashboard }
      : user?.role === 'client'
        ? { to: '/client', label: 'My Gallery', Icon: Image }
        : { to: '/login', label: 'Login', Icon: User };

  // Close the mobile dropdown whenever the route changes (link navigation).
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  // Lock body scroll while the digital dropdown is open + allow Escape to close.
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
      const onKey = (e) => {
        if (e.key === 'Escape') setMenuOpen(false);
      };
      window.addEventListener('keydown', onKey);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', onKey);
      };
    }
    document.body.style.overflow = '';
    return undefined;
  }, [menuOpen]);

  const toggleMenu = () => {
    setMenuOpen((prev) => {
      const next = !prev;
      if (next) setMenuKey((k) => k + 1); // remount content to replay scanline + scramble
      return next;
    });
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 border-b transition-colors duration-500 ${
        scrolled
          ? 'lxra-nav-gradient border-[hsl(var(--lxra-border))] backdrop-blur'
          : 'bg-[#0A0A0A]/60 border-transparent backdrop-blur-sm'
      }`}
      data-testid="site-navbar"
    >
      <nav className="flex w-full items-center justify-between px-4 sm:px-6 lg:px-10 xl:px-14 h-16 lg:h-20">
        <Link to="/" aria-label="LXRA Studios home">
          <Logo />
        </Link>

        <div className="hidden lg:flex items-center gap-5 xl:gap-7">
          {PRIMARY_LINKS.map((link) => {
            const active = isLinkActive(pathname, link.to);
            return (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === '/'}
                data-testid={link.testid}
                className={`relative text-sm tracking-[0.12em] uppercase transition-colors duration-200 py-1 ${
                  active ? 'text-[hsl(var(--lxra-gold))]' : 'text-[hsl(var(--lxra-fg))] hover:text-[hsl(var(--lxra-gold))]'
                }`}
              >
                {link.label}
                {active && (
                  <motion.span
                    layoutId="nav-active-underline"
                    className="absolute left-0 -bottom-1 h-[1px] w-full bg-[hsl(var(--lxra-gold))]"
                    transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                  />
                )}
              </NavLink>
            );
          })}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="More navigation links"
                data-testid={NAVBAR_OVERFLOW.moreTrigger}
                className={`inline-flex items-center gap-1 text-sm tracking-[0.12em] uppercase transition-colors duration-200 py-1 outline-none focus-visible:text-[hsl(var(--lxra-gold))] ${
                  SECONDARY_LINKS.some((l) => isLinkActive(pathname, l.to))
                    ? 'text-[hsl(var(--lxra-gold))]'
                    : 'text-[hsl(var(--lxra-fg))] hover:text-[hsl(var(--lxra-gold))]'
                }`}
              >
                More
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-48 bg-[#141416] border-[hsl(var(--lxra-border))]">
              {SECONDARY_LINKS.map((link) => {
                const active = isLinkActive(pathname, link.to);
                return (
                  <DropdownMenuItem key={link.to} asChild className="cursor-pointer focus:bg-[#1c1c1e]">
                    <Link
                      to={link.to}
                      data-testid={NAVBAR_OVERFLOW.moreItem(link.to)}
                      className={`w-full text-sm tracking-[0.06em] py-1 ${
                        active ? 'text-[hsl(var(--lxra-gold))]' : 'text-[hsl(var(--lxra-fg))]'
                      }`}
                    >
                      {link.label}
                    </Link>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to={accountLink.to}
            aria-label={accountLink.label}
            data-testid="nav-account-link"
            className="group flex items-center gap-2 h-11 px-3.5 rounded-xl border border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] transition-colors duration-200"
          >
            <accountLink.Icon className="h-4 w-4" />
            <span className="text-xs uppercase tracking-[0.08em] font-medium">{accountLink.label}</span>
          </Link>
          <MagneticButton>
            <Link to="/book" data-testid={NAV.bookNow}>
              <Button className="h-11 px-5 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] transition-colors duration-200 tracking-[0.1em] text-xs uppercase font-semibold">
                Book Now
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </MagneticButton>
        </div>

        <button
          type="button"
          onClick={toggleMenu}
          className="lg:hidden relative text-[hsl(var(--lxra-fg))] p-2 -mr-2 transition-colors duration-200 hover:text-[hsl(var(--lxra-gold))]"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="lxra-mobile-dropdown"
          data-testid={NAV.mobileToggle}
        >
          {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {/* Full-screen backdrop behind the digital dropdown */}
      <div
        className={`lxra-dropdown-backdrop lg:hidden fixed inset-0 top-16 bg-black/70 ${menuOpen ? 'lxra-dropdown-open' : ''}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
        data-testid="mobile-menu-backdrop"
      />

      {/* Digital dropdown panel: pixelated/stepped clip-path reveal below the navbar */}
      <div
        id="lxra-mobile-dropdown"
        className={`lxra-dropdown-panel lg:hidden fixed top-16 inset-x-0 z-40 bg-[#0A0A0A] border-b border-[hsl(var(--lxra-border))] ${
          menuOpen ? 'lxra-dropdown-open' : ''
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Site navigation"
        data-testid="mobile-menu-panel"
      >
        <div key={menuKey} className="relative overflow-hidden">
          <MenuScanline />
          <div className="relative z-20 px-6 py-8 flex flex-col gap-6">
            {NAV_LINKS.map((link, i) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === '/'}
                onClick={() => setMenuOpen(false)}
                className={mobileLinkClass}
                data-testid={`${link.testid}-mobile`}
              >
                <TextScramble text={link.label} delay={90 + i * 70} />
              </NavLink>
            ))}
            <Link
              to={accountLink.to}
              onClick={() => setMenuOpen(false)}
              data-testid="nav-account-link-mobile"
              className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] transition-colors duration-200 pt-1"
            >
              <accountLink.Icon className="h-3.5 w-3.5" />
              <TextScramble text={accountLink.label} delay={90 + NAV_LINKS.length * 70} />
            </Link>
            <Link
              to="/book"
              className="mt-2"
              onClick={() => setMenuOpen(false)}
            >
              <Button className="w-full h-11 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] tracking-[0.1em] text-xs uppercase font-semibold">
                <TextScramble text="Book Now" delay={90 + (NAV_LINKS.length + 1) * 70} />
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};
